import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Calculator, Calendar, Sparkles, KeyRound, Heart, Briefcase, Coins, MoreHorizontal } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import UserMenu from '@/components/UserMenu';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { SS_CORE_BASE } from '@/utils/ssMethodData';

/**
 * Reduce a number to a single digit (1-9) by summing its digits repeatedly.
 * Master numbers (11, 22, 33) are NOT preserved — always reduce to 1-9.
 */
function reduceToSingle(num) {
  let n = Math.abs(num);
  while (n > 9) {
    n = String(n).split('').reduce((sum, d) => sum + parseInt(d, 10), 0);
  }
  return n;
}

/**
 * Reduce a full date (DD/MM/YYYY) to a single digit 1-9.
 * Method: sum ALL individual digits of day + month + year, then reduce.
 */
function reduceDateToSingle(day, month, year) {
  const allDigits = `${day}${month}${year}`;
  const digitSum = allDigits.split('').reduce((sum, d) => sum + parseInt(d, 10), 0);
  return reduceToSingle(digitSum);
}

export default function SSMethodPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [eventDay, setEventDay] = useState('');
  const [eventMonth, setEventMonth] = useState('');
  const [eventYear, setEventYear] = useState('');
  const [eventType, setEventType] = useState('start'); // 'start' or 'end'
  const [birthDay, setBirthDay] = useState(user?.day ? String(user.day) : '');
  const [birthMonth, setBirthMonth] = useState(user?.month ? String(user.month) : '');
  const [birthYear, setBirthYear] = useState(user?.year ? String(user.year) : '');
  const [category, setCategory] = useState('relationships');
  const [result, setResult] = useState(null);

  const CATEGORIES = [
    { id: 'relationships', label: 'Santykiai', icon: Heart },
    { id: 'career', label: 'Karjera', icon: Briefcase },
    { id: 'finance', label: 'Finansai', icon: Coins },
    { id: 'other', label: 'Kita', icon: MoreHorizontal },
  ];

  const handleCalculate = () => {
    const eDay = parseInt(eventDay, 10);
    const eMonth = parseInt(eventMonth, 10);
    const eYear = parseInt(eventYear, 10);
    const bDay = parseInt(birthDay, 10);
    const bMonth = parseInt(birthMonth, 10);
    const bYear = parseInt(birthYear, 10);

    if (!eDay || !eMonth || !eYear || eDay < 1 || eDay > 31 || eMonth < 1 || eMonth > 12 || eYear < 1900 || eYear > 2100) {
      toast.error('Įveskite teisingą įvykio datą');
      return;
    }
    if (!bDay || !bMonth || !bYear || bDay < 1 || bDay > 31 || bMonth < 1 || bMonth > 12 || bYear < 1900 || bYear > 2100) {
      toast.error('Įveskite teisingą gimimo datą');
      return;
    }

    const stepA = reduceDateToSingle(eDay, eMonth, eYear);
    const stepB = reduceDateToSingle(bDay, bMonth, bYear);
    const rawSum = stepA + stepB;
    const interactionNumber = reduceToSingle(rawSum);

    setResult({
      stepA,
      stepB,
      rawSum,
      interactionNumber,
      eventDate: `${String(eDay).padStart(2, '0')}/${String(eMonth).padStart(2, '0')}/${eYear}`,
      birthDate: `${String(bDay).padStart(2, '0')}/${String(bMonth).padStart(2, '0')}/${bYear}`,
      eventType,
      category,
    });
  };

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-foreground/70 hover:text-primary"
            data-testid="ss-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Grįžti
          </Button>
          <UserMenu />
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="font-display text-3xl sm:text-4xl bg-gradient-to-r from-amber-400 via-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
            SS Metodo Analizė
          </h1>
          <p className="text-muted-foreground text-sm">
            Sąveikos skaičius tarp įvykio ir asmens
          </p>
        </div>

        {/* Input Card */}
        <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/20 p-6 mb-6">
          {/* Event Date */}
          <div className="mb-6">
            <Label className="text-sm text-[#d4af37] font-semibold flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4" />
              Įvykio data
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Diena</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={eventDay}
                  onChange={(e) => setEventDay(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="DD"
                  data-testid="event-day"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Mėnuo</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={eventMonth}
                  onChange={(e) => setEventMonth(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="MM"
                  data-testid="event-month"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Metai</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={eventYear}
                  onChange={(e) => setEventYear(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="YYYY"
                  data-testid="event-year"
                />
              </div>
            </div>
          </div>

          {/* Event Type Toggle */}
          <div className="mb-6">
            <Label className="text-sm text-[#d4af37] font-semibold mb-3 block">
              Įvykio tipas
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setEventType('start')}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  eventType === 'start'
                    ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                    : 'border-[#d4af37]/30 bg-[#0f0f23]/50 text-muted-foreground hover:border-emerald-500/50'
                }`}
                data-testid="event-type-start"
              >
                <span className="text-sm font-medium">Pradžia</span>
              </button>
              <button
                type="button"
                onClick={() => setEventType('end')}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  eventType === 'end'
                    ? 'border-rose-500 bg-rose-500/20 text-rose-400'
                    : 'border-[#d4af37]/30 bg-[#0f0f23]/50 text-muted-foreground hover:border-rose-500/50'
                }`}
                data-testid="event-type-end"
              >
                <span className="text-sm font-medium">Pabaiga</span>
              </button>
            </div>
          </div>

          {/* Category Selector */}
          <div className="mb-6">
            <Label className="text-sm text-[#d4af37] font-semibold mb-3 block">
              Gyvenimo sfera
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isActive = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all flex flex-col items-center gap-1.5 ${
                      isActive
                        ? 'border-[#d4af37] bg-[#d4af37]/15 text-[#d4af37]'
                        : 'border-[#d4af37]/20 bg-[#0f0f23]/50 text-muted-foreground hover:border-[#d4af37]/50'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Birth Date */}
          <div className="mb-6">
            <Label className="text-sm text-[#d4af37] font-semibold flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4" />
              Gimimo data
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Diena</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="DD"
                  data-testid="birth-day"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Mėnuo</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="MM"
                  data-testid="birth-month"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Metai</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ''))}
                  className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                  placeholder="YYYY"
                  data-testid="birth-year"
                />
              </div>
            </div>
          </div>

          {/* Calculate Button */}
          <Button
            onClick={handleCalculate}
            className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
            data-testid="ss-calculate-button"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Skaičiuoti
          </Button>
        </Card>

        {/* Result */}
        {result && (
          <>
            <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/30 p-6 mb-6" data-testid="ss-result-card">
              {/* Interaction Number - Large Display */}
              <div className="text-center mb-6">
                <p className="text-sm text-muted-foreground mb-2">Sąveikos skaičius</p>
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#d4af37]/30 to-[#d4af37]/10 border-2 border-[#d4af37]/60">
                  <span className="text-4xl font-bold text-[#d4af37]" data-testid="interaction-number">
                    {result.interactionNumber}
                  </span>
                </div>
              </div>

              {/* Calculation Steps */}
              <div className="space-y-3 bg-[#0f0f23]/40 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" />
                  Skaičiavimo žingsniai
                </h3>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    A: Įvykio data ({result.eventDate})
                  </span>
                  <span className="text-white font-mono font-bold" data-testid="step-a-value">{result.stepA}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    B: Gimimo data ({result.birthDate})
                  </span>
                  <span className="text-white font-mono font-bold" data-testid="step-b-value">{result.stepB}</span>
                </div>

                <div className="border-t border-[#d4af37]/20 pt-3 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    C: A + B = {result.stepA} + {result.stepB} = {result.rawSum}
                    {result.rawSum > 9 ? ` \u2192 ${result.interactionNumber}` : ''}
                  </span>
                  <span className="text-[#d4af37] font-mono font-bold text-lg" data-testid="step-c-value">{result.interactionNumber}</span>
                </div>

                <div className="border-t border-[#d4af37]/20 pt-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Tipas: {result.eventType === 'start' ? 'Pradžia' : 'Pabaiga'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Sfera: {CATEGORIES.find(c => c.id === result.category)?.label}
                  </span>
                </div>
              </div>
            </Card>

            {/* Core Base Meaning */}
            {SS_CORE_BASE[result.interactionNumber] && (
              <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/20 p-6 mb-6" data-testid="ss-base-meaning-card">
                <h3 className="text-lg font-semibold text-[#d4af37] mb-4 text-center">
                  Skaičiaus {result.interactionNumber} esmė
                </h3>

                {/* Base Meaning Text */}
                <div className="text-foreground/90 text-sm leading-relaxed whitespace-pre-line mb-6" data-testid="ss-base-meaning-text">
                  {SS_CORE_BASE[result.interactionNumber].base_meaning}
                </div>

                {/* Key Words */}
                <div className="bg-[#0f0f23]/40 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2 mb-3">
                    <KeyRound className="w-4 h-4" />
                    Pagrindiniai raktai
                  </h4>
                  <div className="flex flex-wrap gap-2" data-testid="ss-keys-list">
                    {SS_CORE_BASE[result.interactionNumber].keys.map((key, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30"
                      >
                        {key}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
